import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  List, Plus, Eye, Trash2, Download, Search, Users, Filter,
  BookOpen, ChevronLeft, ChevronRight, UserPlus, X, Printer, FileSpreadsheet, FileText, Edit, Utensils, Upload, Loader2, FolderOpen, Folder, UserCog, FolderPlus, ChevronDown, ChevronUp, MoreVertical, Move, FolderInput
} from 'lucide-react';
import { ListExportDialog } from '../components/ListExportDialog';
import { FilterMembersDialog } from '../components/FilterMembersDialog';

export default function ListsPage() {
  const { api, isAdmin, user } = useAuth();
  // Check if user is ONLY a list_manager (not admin)
  const isListManager = !isAdmin && (user?.role === 'list_manager' || user?.roles?.includes('list_manager'));
  const [customLists, setCustomLists] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showFilterDialog, setShowFilterDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showCreateFolderDialog, setShowCreateFolderDialog] = useState(false);
  const [showManageFolderDialog, setShowManageFolderDialog] = useState(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderTab, setNewFolderTab] = useState('custom');
  const [viewingList, setViewingList] = useState(null);
  const [editingList, setEditingList] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadListName, setUploadListName] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [listSearchQuery, setListSearchQuery] = useState('');
  const [collapsedFolders, setCollapsedFolders] = useState({});
  const initialFetchDone = useRef(false);

  const fetchLists = useCallback(async () => {
    try {
      const [listsRes, foldersRes] = await Promise.all([
        api.get('/lists'),
        api.get('/list-folders')
      ]);
      const lists = listsRes.data.lists || [];
      const uniqueLists = lists.filter((list, index, self) => 
        index === self.findIndex(l => l.id === list.id)
      );
      setCustomLists(uniqueLists);
      setFolders(foldersRes.data.folders || []);
    } catch (error) {
      toast.error('Failed to fetch lists');
    } finally {
      setLoading(false);
    }
  }, [api]);

  const toggleFolder = (folderId) => {
    setCollapsedFolders(prev => ({...prev, [folderId]: !prev[folderId]}));
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) { toast.error('Enter folder name'); return; }
    try {
      await api.post('/list-folders', { name: newFolderName.trim(), tab: newFolderTab });
      toast.success('Folder created');
      setShowCreateFolderDialog(false);
      setNewFolderName('');
      fetchLists();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create folder');
    }
  };

  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm('Delete this folder? Lists inside will be moved to unfiled.')) return;
    try {
      await api.delete(`/list-folders/${folderId}`);
      toast.success('Folder deleted');
      setShowManageFolderDialog(null);
      fetchLists();
    } catch (error) {
      toast.error('Failed to delete folder');
    }
  };

  const handleMoveListToFolder = async (listId, folderName) => {
    try {
      await api.put(`/lists/${listId}/move-to-folder`, { folder: folderName });
      toast.success(folderName ? `Moved to ${folderName}` : 'Moved to unfiled');
      fetchLists();
    } catch (error) {
      toast.error('Failed to move list');
    }
  };

  const handleUpdateAutoFolder = async (folderId, autoListTypes) => {
    try {
      await api.put(`/list-folders/${folderId}`, { auto_list_types: autoListTypes });
      toast.success('Folder updated');
      fetchLists();
    } catch (error) {
      toast.error('Failed to update folder');
    }
  };

  // Initial fetch - only run once on mount
  useEffect(() => {
    if (!initialFetchDone.current) {
      initialFetchDone.current = true;
      fetchLists();
    }
  }, [fetchLists]);

  const handleDeleteList = async (listId) => {
    if (!window.confirm('Are you sure you want to delete this list?')) return;
    try {
      await api.delete(`/lists/${listId}`);
      toast.success('List deleted');
      fetchLists();
    } catch (error) {
      toast.error('Failed to delete list');
    }
  };

  const handleUploadFile = async () => {
    if (!uploadFile || !uploadListName.trim()) {
      toast.error('Please select a file and enter a list name');
      return;
    }
    
    setUploading(true);
    setUploadResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      
      const response = await api.post(
        `/lists/upload?list_name=${encodeURIComponent(uploadListName.trim())}&description=${encodeURIComponent(uploadDescription.trim())}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setUploadResult(response.data);
      toast.success(response.data.message);
      fetchLists();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Upload failed';
      toast.error(detail);
      setUploadResult({ error: detail });
    } finally {
      setUploading(false);
    }
  };

  const resetUploadDialog = () => {
    setShowUploadDialog(false);
    setUploadFile(null);
    setUploadListName('');
    setUploadDescription('');
    setUploadResult(null);
  };

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in" data-testid="lists-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-semibold text-slate-800">Lists</h1>
          <p className="text-xs sm:text-sm text-slate-500 hidden sm:block">Auto & custom lists</p>
        </div>
        <div className="flex flex-wrap gap-1 sm:gap-2">
          {!isListManager && (
            <Button 
              onClick={() => setShowFilterDialog(true)} 
              variant="outline"
              className="text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3" 
              size="sm"
            >
              <Filter className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline">Filter & Export</span>
            </Button>
          )}
          {(isAdmin || isListManager) && (
            <>
              <Button onClick={() => setShowCreateFolderDialog(true)} variant="outline" className="text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3" size="sm">
                <FolderPlus className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                <span className="hidden sm:inline">New Folder</span>
              </Button>
              <Button onClick={() => setShowUploadDialog(true)} variant="outline" className="text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3" size="sm">
                <Upload className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                <span className="hidden sm:inline">Upload</span>
              </Button>
              <Button onClick={() => setShowCreateDialog(true)} className="bg-navy hover:bg-navy-light text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3" size="sm">
                <Plus className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                <span className="hidden sm:inline">Create</span>
              </Button>
            </>
          )}
        </div>
      </div>

      <Tabs defaultValue={isListManager ? "custom" : "auto"} className="space-y-4">
        <TabsList className="bg-slate-100 w-full sm:w-auto">
          {!isListManager && (
            <TabsTrigger value="auto" className="flex-1 sm:flex-none data-[state=active]:bg-navy data-[state=active]:text-white text-xs sm:text-sm">
              <BookOpen className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              Auto Lists
            </TabsTrigger>
          )}
          <TabsTrigger value="custom" className="flex-1 sm:flex-none data-[state=active]:bg-navy data-[state=active]:text-white text-xs sm:text-sm">
            <List className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            Custom Lists
          </TabsTrigger>
        </TabsList>

        {/* Auto Lists Tab - Hidden for list_manager */}
        {!isListManager && (
        <TabsContent value="auto">
          {(() => {
            const ALL_AUTO_LISTS = [
              { type: 'mashaaikh', title: 'Mashaaikh', description: "Names starting with 'Shaikh'", icon: <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-500" />, endpoint: '/lists/auto/mashaaikh' },
              { type: 'mafasiyah', title: 'Mafasiyah', description: "Names starting with 'Mulla'", icon: <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-purple-500" />, endpoint: '/lists/auto/mafasi' },
              { type: 'fmb-all', title: 'FMB - All', description: 'All families (Going & Not Going)', icon: <Utensils className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500" />, endpoint: '/lists/auto/fmb-all' },
              { type: 'fmb-going', title: 'FMB - Going', description: 'Families taking Thaali', icon: <Utensils className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-500" />, endpoint: '/lists/auto/fmb-going' },
              { type: 'fmb-not-going', title: 'FMB - Not Going', description: 'Families not taking Thaali', icon: <Utensils className="w-5 h-5 sm:w-6 sm:h-6 text-amber-500" />, endpoint: '/lists/auto/fmb-not-going' },
              { type: 'managers', title: 'Managers Jamaat', description: 'Jamaat Managers / Coordinators', icon: <Users className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500" />, endpoint: '/lists/auto/managers' },
              { type: 'mustashareen', title: 'Mustashareen Jamaat', description: 'Advisory Committee', icon: <Users className="w-5 h-5 sm:w-6 sm:h-6 text-amber-500" />, endpoint: '/lists/auto/mustashareen' },
              { type: 'masool', title: 'Masool', description: 'Mohalla Male Leaders', icon: <Users className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-500" />, endpoint: '/lists/auto/masool' },
              { type: 'masoola', title: 'Masoola', description: 'Mohalla Female Leaders', icon: <Users className="w-5 h-5 sm:w-6 sm:h-6 text-pink-500" />, endpoint: '/lists/auto/masoola' },
              { type: 'musaid', title: 'Musaid', description: 'Sector Musaid (Male Assistants)', icon: <Users className="w-5 h-5 sm:w-6 sm:h-6 text-teal-500" />, endpoint: '/lists/auto/musaid' },
              { type: 'musaida', title: 'Musaida', description: 'Sector Musaida (Female Assistants)', icon: <Users className="w-5 h-5 sm:w-6 sm:h-6 text-rose-500" />, endpoint: '/lists/auto/musaida' },
              { type: 'fmb-incharge', title: 'FMB Incharge', description: 'Mohalla FMB Incharges', icon: <Users className="w-5 h-5 sm:w-6 sm:h-6 text-orange-500" />, endpoint: '/lists/auto/fmb-incharge' },
              { type: 'umoor-coordinators', title: 'Umoor Coordinators', description: 'Jamaat Umoor Coordinators', icon: <Users className="w-5 h-5 sm:w-6 sm:h-6 text-cyan-500" />, endpoint: '/lists/auto/umoor-coordinators' },
            ];

            const autoFolders = folders.filter(f => f.tab === 'auto');
            const assignedTypes = new Set(autoFolders.flatMap(f => f.auto_list_types || []));
            const unfiledLists = ALL_AUTO_LISTS.filter(l => !assignedTypes.has(l.type));

            return (
              <div className="space-y-4">
                {/* Auto folders */}
                {autoFolders.map(folder => {
                  const folderLists = ALL_AUTO_LISTS.filter(l => (folder.auto_list_types || []).includes(l.type));
                  const isCollapsed = collapsedFolders[folder.id];
                  return (
                    <div key={folder.id} className="border border-slate-200 rounded-lg overflow-hidden">
                      <div 
                        className="flex items-center justify-between px-4 py-3 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
                        onClick={() => toggleFolder(folder.id)}
                      >
                        <div className="flex items-center gap-2">
                          {isCollapsed ? <ChevronRight className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                          <FolderOpen className="w-5 h-5 text-amber-500" />
                          <span className="font-semibold text-slate-700">{folder.name}</span>
                          <Badge variant="secondary" className="text-xs">{folderLists.length}</Badge>
                        </div>
                        {isAdmin && (
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setShowManageFolderDialog(folder); }} className="h-7 w-7 p-0">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      {!isCollapsed && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 p-4">
                          {folderLists.map(al => (
                            <AutoListCard key={al.type} title={al.title} description={al.description} icon={al.icon} api={api} endpoint={al.endpoint} listType={al.type} />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Unfiled auto lists */}
                {unfiledLists.length > 0 && (
                  <div>
                    {autoFolders.length > 0 && (
                      <div className="flex items-center gap-2 mb-3">
                        <List className="w-5 h-5 text-slate-400" />
                        <span className="font-semibold text-slate-600">Unfiled</span>
                        <Badge variant="secondary" className="text-xs">{unfiledLists.length}</Badge>
                      </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                      {unfiledLists.map(al => (
                        <AutoListCard key={al.type} title={al.title} description={al.description} icon={al.icon} api={api} endpoint={al.endpoint} listType={al.type} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </TabsContent>
        )}

        {/* Custom Lists Tab */}
        <TabsContent value="custom">
          <Card className="border-0 shadow-card">
            <CardContent className="p-4 sm:p-6">
              {/* Search Bar */}
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search lists by name..."
                    value={listSearchQuery}
                    onChange={(e) => setListSearchQuery(e.target.value)}
                    className="pl-9"
                    data-testid="list-search-input"
                  />
                  {listSearchQuery && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                      onClick={() => setListSearchQuery('')}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>

              {loading ? (
                <div className="text-center py-8">
                  <div className="w-6 h-6 border-2 border-navy border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
              ) : customLists.length === 0 ? (
                <div className="text-center py-8">
                  <List className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">No custom lists created yet</p>
                  <Button onClick={() => setShowCreateDialog(true)} className="mt-4 bg-navy">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Your First List
                  </Button>
                </div>
              ) : (() => {
                const customFolders = folders.filter(f => f.tab === 'custom');
                const allFiltered = customLists.filter(list => {
                  const matchesSearch = list.name.toLowerCase().includes(listSearchQuery.toLowerCase()) ||
                    (list.description && list.description.toLowerCase().includes(listSearchQuery.toLowerCase()));
                  return matchesSearch;
                });
                const folderNames = new Set(customFolders.map(f => f.name));
                const unfiledLists = allFiltered.filter(l => !l.folder || !folderNames.has(l.folder));
                
                return (
                  <div className="space-y-4">
                    {/* Custom folders */}
                    {customFolders.map(folder => {
                      const folderLists = allFiltered.filter(l => l.folder === folder.name);
                      const isCollapsed = collapsedFolders[folder.id];
                      return (
                        <div key={folder.id} className="border border-slate-200 rounded-lg overflow-hidden">
                          <div 
                            className="flex items-center justify-between px-4 py-3 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
                            onClick={() => toggleFolder(folder.id)}
                          >
                            <div className="flex items-center gap-2">
                              {isCollapsed ? <ChevronRight className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                              <FolderOpen className="w-5 h-5 text-amber-500" />
                              <span className="font-semibold text-slate-700">{folder.name}</span>
                              <Badge variant="secondary" className="text-xs">{folderLists.length}</Badge>
                            </div>
                            {isAdmin && (
                              <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setShowManageFolderDialog(folder); }} className="h-7 w-7 p-0">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                          {!isCollapsed && (
                            <div className="p-4">
                              {folderLists.length === 0 ? (
                                <p className="text-sm text-slate-400 text-center py-4">No lists in this folder</p>
                              ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                  {folderLists.map(list => (
                                    <CustomListCard 
                                      key={list.id} 
                                      list={list} 
                                      api={api}
                                      isAdmin={isAdmin}
                                      onView={() => setViewingList(list)}
                                      onEdit={() => setEditingList(list)}
                                      onDelete={() => handleDeleteList(list.id)}
                                      onRefresh={fetchLists}
                                      folders={customFolders}
                                      onMoveToFolder={handleMoveListToFolder}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Unfiled custom lists */}
                    {unfiledLists.length > 0 && (
                      <div>
                        {customFolders.length > 0 && (
                          <div className="flex items-center gap-2 mb-3">
                            <List className="w-5 h-5 text-slate-400" />
                            <span className="font-semibold text-slate-600">Unfiled</span>
                            <Badge variant="secondary" className="text-xs">{unfiledLists.length}</Badge>
                          </div>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {unfiledLists.map(list => (
                            <CustomListCard 
                              key={list.id} 
                              list={list} 
                              api={api}
                              isAdmin={isAdmin}
                              onView={() => setViewingList(list)}
                              onEdit={() => setEditingList(list)}
                              onDelete={() => handleDeleteList(list.id)}
                              onRefresh={fetchLists}
                              folders={customFolders}
                              onMoveToFolder={handleMoveListToFolder}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {allFiltered.length === 0 && (
                      <div className="text-center py-8">
                        <Search className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                        <p className="text-slate-500">No lists found matching "{listSearchQuery}"</p>
                        <Button variant="outline" onClick={() => setListSearchQuery('')} className="mt-4">
                          Clear Search
                        </Button>
                      </div>
                    )}
                    
                    {listSearchQuery && allFiltered.length > 0 && (
                      <p className="text-xs text-slate-500 mt-3 text-center">
                        Showing {allFiltered.length} of {customLists.length} lists
                      </p>
                    )}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* View List Dialog */}
      {viewingList && (
        <ViewListDialog 
          list={viewingList} 
          onClose={() => setViewingList(null)} 
          api={api}
        />
      )}

      {/* Edit List Dialog */}
      {editingList && (
        <EditListDialog
          list={editingList}
          api={api}
          onClose={() => setEditingList(null)}
          onUpdated={fetchLists}
        />
      )}

      {/* Create List Dialog */}
      {showCreateDialog && (
        <CreateListDialog
          api={api}
          onClose={() => setShowCreateDialog(false)}
          onCreated={fetchLists}
          isAdmin={isAdmin}
          existingFolders={folders.filter(f => f.tab === 'custom')}
        />
      )}

      {/* Filter & Export Dialog */}
      <FilterMembersDialog 
        open={showFilterDialog}
        onOpenChange={setShowFilterDialog}
        api={api}
      />

      {/* Upload List Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={(open) => !open && resetUploadDialog()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Upload List from File</DialogTitle>
            <DialogDescription>
              Upload an Excel (.xlsx), CSV, or PDF file containing ITS IDs or member names.
              The system will match them with existing members in the database.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>List Name *</Label>
              <Input
                value={uploadListName}
                onChange={(e) => setUploadListName(e.target.value)}
                placeholder="e.g., Managers Jamaat"
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">
                If a list with this name exists, members will be added to it.
              </p>
            </div>
            
            <div>
              <Label>Description</Label>
              <Input
                value={uploadDescription}
                onChange={(e) => setUploadDescription(e.target.value)}
                placeholder="Optional description"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Select File *</Label>
              <div className="mt-1 flex items-center gap-2">
                <Input
                  type="file"
                  accept=".xlsx,.xls,.csv,.pdf"
                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                  className="flex-1"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Supported: Excel (.xlsx, .xls), CSV (.csv), PDF (.pdf)
              </p>
            </div>
            
            {uploadResult && !uploadResult.error && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm font-medium text-green-800">
                  ✓ {uploadResult.matched_count} members matched
                </p>
                {uploadResult.custom_columns_added > 0 && (
                  <p className="text-sm text-blue-700 mt-1">
                    📊 {uploadResult.custom_columns_added} custom column(s) added: {uploadResult.custom_columns?.join(', ')}
                  </p>
                )}
                {uploadResult.unmatched_count > 0 && (
                  <p className="text-xs text-amber-700 mt-1">
                    {uploadResult.unmatched_count} values not matched: {uploadResult.unmatched_samples?.join(', ')}
                  </p>
                )}
                {uploadResult.matched_members?.length > 0 && (
                  <div className="mt-2 text-xs text-slate-600">
                    <p className="font-medium">Sample matches:</p>
                    <ul className="list-disc list-inside">
                      {uploadResult.matched_members.slice(0, 5).map((m, i) => (
                        <li key={i}>{m.name} ({m.its_id})</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            
            {uploadResult?.error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800">{uploadResult.error}</p>
              </div>
            )}
          </div>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={resetUploadDialog}>
              {uploadResult && !uploadResult.error ? 'Done' : 'Cancel'}
            </Button>
            {(!uploadResult || uploadResult.error) && (
              <Button 
                onClick={handleUploadFile} 
                disabled={uploading || !uploadFile || !uploadListName.trim()}
                className="bg-navy"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload & Process
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Folder Dialog */}
      <Dialog open={showCreateFolderDialog} onOpenChange={setShowCreateFolderDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Create New Folder</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium text-slate-700">Folder Name</label>
              <Input 
                value={newFolderName} 
                onChange={(e) => setNewFolderName(e.target.value)} 
                placeholder="Enter folder name..."
                data-testid="folder-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Tab</label>
              <Select value={newFolderTab} onValueChange={setNewFolderTab}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto Lists</SelectItem>
                  <SelectItem value="custom">Custom Lists</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateFolderDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateFolder} className="bg-navy hover:bg-navy-light" data-testid="create-folder-btn">Create Folder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Folder Dialog */}
      {showManageFolderDialog && (
        <Dialog open={true} onOpenChange={() => setShowManageFolderDialog(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Manage Folder: {showManageFolderDialog.name}</DialogTitle>
            </DialogHeader>
            <ManageFolderContent 
              folder={showManageFolderDialog}
              api={api}
              onDelete={() => handleDeleteFolder(showManageFolderDialog.id)}
              onUpdate={(data) => {
                if (data.auto_list_types !== undefined) {
                  handleUpdateAutoFolder(showManageFolderDialog.id, data.auto_list_types);
                }
                if (data.name) {
                  api.put(`/list-folders/${showManageFolderDialog.id}`, { name: data.name })
                    .then(() => { toast.success('Folder renamed'); fetchLists(); setShowManageFolderDialog(null); })
                    .catch(() => toast.error('Failed to rename'));
                }
              }}
              onClose={() => setShowManageFolderDialog(null)}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// Custom List Card with Export functionality
function CustomListCard({ list, api, isAdmin, onView, onEdit, onDelete, onRefresh, folders = [], onMoveToFolder }) {
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showManagerDialog, setShowManagerDialog] = useState(false);
  const [showMoveMenu, setShowMoveMenu] = useState(false);
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  const handleExportClick = async () => {
    // Fetch members data for export
    setLoadingMembers(true);
    try {
      const response = await api.get(`/lists/${list.id}`);
      setMembers(response.data.members || []);
      setShowExportDialog(true);
    } catch (error) {
      console.error('Failed to load list data:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to load list data: ${errorMsg}`);
    } finally {
      setLoadingMembers(false);
    }
  };

  return (
    <>
      <Card className="border shadow-sm hover:shadow-md transition-shadow">
        <CardContent className="p-3 sm:p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-sm sm:text-base truncate">{list.name}</h3>
              <p className="text-xs sm:text-sm text-slate-500 mt-1 line-clamp-2">{list.description || 'No description'}</p>
              <div className="flex flex-wrap gap-1 mt-2">
                <Badge className="text-xs">{list.member_count} members</Badge>
                {list.folder && (
                  <Badge variant="outline" className="text-xs bg-slate-50">
                    <Folder className="w-3 h-3 mr-1" />
                    {list.folder}
                  </Badge>
                )}
                {list.assigned_managers?.length > 0 && (
                  <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">
                    <UserCog className="w-3 h-3 mr-1" />
                    {list.assigned_managers.length} manager(s)
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-1 sm:gap-2 mt-3 sm:mt-4">
            <Button variant="outline" size="sm" onClick={onView} className="text-xs h-8 px-2 sm:px-3">
              <Eye className="w-3 h-3 sm:mr-1" />
              <span className="hidden sm:inline">View</span>
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportClick} disabled={loadingMembers} className="text-xs h-8 px-2 sm:px-3">
              <Download className="w-3 h-3 sm:mr-1" />
              <span className="hidden sm:inline">{loadingMembers ? '...' : 'Export'}</span>
            </Button>
            {isAdmin && (
              <>
                <Button variant="outline" size="sm" onClick={() => setShowManagerDialog(true)} className="text-blue-600 text-xs h-8 px-2 sm:px-3">
                  <UserCog className="w-3 h-3 sm:mr-1" />
                  <span className="hidden sm:inline">Access</span>
                </Button>
                <Button variant="outline" size="sm" onClick={onEdit} className="text-blue-600 text-xs h-8 px-2 sm:px-3">
                  <Edit className="w-3 h-3 sm:mr-1" />
                  <span className="hidden sm:inline">Edit</span>
                </Button>
                <Button variant="outline" size="sm" onClick={onDelete} className="text-red-600 text-xs h-8 px-2">
                  <Trash2 className="w-3 h-3" />
                </Button>
                {folders.length > 0 && onMoveToFolder && (
                  <div className="relative">
                    <Button variant="outline" size="sm" onClick={() => setShowMoveMenu(!showMoveMenu)} className="text-xs h-8 px-2 sm:px-3">
                      <FolderInput className="w-3 h-3 sm:mr-1" />
                      <span className="hidden sm:inline">Move</span>
                    </Button>
                    {showMoveMenu && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setShowMoveMenu(false)} />
                        <div className="absolute left-0 bottom-full mb-1 z-50 bg-white border rounded-md shadow-lg py-1 min-w-[180px]">
                          {list.folder && (
                            <button 
                              className="w-full text-left px-3 py-2 text-xs hover:bg-slate-100 text-slate-500"
                              onClick={() => { onMoveToFolder(list.id, ''); setShowMoveMenu(false); }}
                            >
                              Remove from folder
                            </button>
                          )}
                          {folders.map(f => (
                            f.name !== list.folder && (
                              <button 
                                key={f.id} 
                                className="w-full text-left px-3 py-2 text-xs hover:bg-slate-100 flex items-center gap-2"
                                onClick={() => { onMoveToFolder(list.id, f.name); setShowMoveMenu(false); }}
                              >
                                <FolderOpen className="w-3 h-3 text-amber-500" />
                                {f.name}
                              </button>
                            )
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Export Dialog */}
      <ListExportDialog 
        open={showExportDialog}
        onOpenChange={setShowExportDialog}
        listType={list.name}
        listData={members}
      />
      
      {/* Manager Access Dialog */}
      {showManagerDialog && (
        <ManageListAccessDialog
          list={list}
          api={api}
          onClose={() => setShowManagerDialog(false)}
          onUpdated={onRefresh}
        />
      )}
    </>
  );
}

function AutoListCard({ title, description, icon, api, endpoint, listType }) {
  const { isAdmin } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showView, setShowView] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [allMembers, setAllMembers] = useState([]);
  const [loadingAllMembers, setLoadingAllMembers] = useState(false);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddMember, setShowAddMember] = useState(false);
  const [memberSearchTerm, setMemberSearchTerm] = useState('');
  const [memberSearchResults, setMemberSearchResults] = useState([]);
  const [searchingMembers, setSearchingMembers] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [removingMember, setRemovingMember] = useState(null);
  const limit = 20;

  // Determine column headers based on list type
  const isLeadershipList = ['masool', 'masoola', 'musaid', 'musaida', 'fmb-incharge'].includes(listType);
  const isSectorBasedList = ['musaid', 'musaida'].includes(listType);
  const isFMBList = ['fmb-all', 'fmb-going', 'fmb-not-going'].includes(listType);

  useEffect(() => {
    fetchData();
  }, [page]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`${endpoint}?page=${page}&limit=${limit}`);
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch list');
    } finally {
      setLoading(false);
    }
  };

  // Fetch ALL members for export (no pagination limit)
  const handleExportClick = async () => {
    try {
      setLoadingAllMembers(true);
      const response = await api.get(`${endpoint}?page=1&limit=5000`);
      setAllMembers(response.data.members || []);
      setShowExportDialog(true);
    } catch (error) {
      console.error('Failed to fetch all members for export');
      toast.error('Failed to prepare export');
    } finally {
      setLoadingAllMembers(false);
    }
  };

  // Real-time search members to add - searches by partial name or ITS
  const searchMembers = async (term) => {
    if (term.length < 1) {
      setMemberSearchResults([]);
      return;
    }
    try {
      setSearchingMembers(true);
      // Use fuzzy search endpoint for partial matching
      const response = await api.get(`/members/search-fuzzy?q=${encodeURIComponent(term)}&limit=30`);
      // Filter out members already in the list
      const existingIds = (data.members || []).map(m => m.id || m.its_id);
      const existingIts = (data.members || []).map(m => m.its_id);
      const filtered = (response.data.members || []).filter(m => 
        !existingIds.includes(m.id) && !existingIts.includes(m.its_id)
      );
      setMemberSearchResults(filtered);
    } catch (error) {
      console.error('Search failed');
      setMemberSearchResults([]);
    } finally {
      setSearchingMembers(false);
    }
  };

  // Debounced search on input change
  useEffect(() => {
    const timer = setTimeout(() => {
      if (memberSearchTerm) {
        searchMembers(memberSearchTerm);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [memberSearchTerm]);

  // Add members to list - creates custom list if needed
  const handleAddMembers = async () => {
    if (selectedMembers.length === 0) return;
    
    try {
      // Try to find existing custom list for this type
      const listsResponse = await api.get('/lists');
      let matchingList = (listsResponse.data.lists || []).find(l => 
        l.name.toLowerCase().includes(listType.replace('-', ' ')) ||
        l.name.toLowerCase() === title.toLowerCase()
      );
      
      // If no list exists, create one
      if (!matchingList) {
        const createResponse = await api.post('/lists', {
          name: title,
          description: `Auto-created list for ${title}`
        });
        matchingList = createResponse.data;
      }

      await api.post(`/lists/${matchingList.id}/members`, {
        member_ids: selectedMembers.map(m => m.id)
      });
      
      toast.success(`Added ${selectedMembers.length} member(s) to ${title}`);
      setSelectedMembers([]);
      setMemberSearchTerm('');
      setMemberSearchResults([]);
      setShowAddMember(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to add members');
    }
  };

  // Remove member from list
  const handleRemoveMember = async (memberId, memberItsId) => {
    try {
      setRemovingMember(memberId || memberItsId);
      
      // Get the custom list ID for this list type
      const listsResponse = await api.get('/lists');
      const matchingList = (listsResponse.data.lists || []).find(l => 
        l.name.toLowerCase().includes(listType.replace('-', ' ')) ||
        l.name.toLowerCase() === title.toLowerCase()
      );
      
      if (!matchingList) {
        toast.error(`This is an auto-generated list. To remove members, update the ${isLeadershipList ? 'Mohalla/Sector leadership settings' : 'source data'}.`);
        return;
      }

      await api.delete(`/lists/${matchingList.id}/members/${memberId}`);
      toast.success('Member removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove member');
    } finally {
      setRemovingMember(null);
    }
  };

  // Toggle member selection for adding
  const toggleMemberSelection = (member) => {
    setSelectedMembers(prev => 
      prev.find(m => m.id === member.id)
        ? prev.filter(m => m.id !== member.id)
        : [...prev, member]
    );
  };

  // Filter members by search query (name, ITS, mobile) - real-time partial matching
  const filteredMembers = (data.members || []).filter(member => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      (member.full_name || '').toLowerCase().includes(query) ||
      (member.its_id || '').toString().includes(query) ||
      (member.mobile || '').includes(query)
    );
  });

  const totalPages = Math.ceil((data.total || 0) / limit);

  return (
    <>
      <Card className="border-0 shadow-card">
        <CardHeader className="p-3 sm:p-4 pb-2">
          <div className="flex items-start sm:items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <div className="shrink-0">{icon}</div>
              <div className="min-w-0">
                <CardTitle className="text-sm sm:text-lg truncate">{title}</CardTitle>
                <p className="text-xs sm:text-sm text-slate-500 truncate">{description}</p>
              </div>
            </div>
            <Badge className="bg-navy text-white text-xs sm:text-sm shrink-0">{data.total || 0}</Badge>
          </div>
        </CardHeader>
        <CardContent className="p-3 sm:p-4 pt-2">
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowView(true)} className="text-xs sm:text-sm">
              <Eye className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline">View</span>
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleExportClick}
              disabled={loadingAllMembers}
              className="text-xs sm:text-sm"
            >
              <Download className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline">{loadingAllMembers ? 'Loading...' : 'Export'}</span>
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.print()} className="text-xs sm:text-sm hidden md:flex">
              <Printer className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline">Print</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* View Dialog with Edit/Add/Remove capabilities */}
      <Dialog open={showView} onOpenChange={(open) => { setShowView(open); if (!open) { setSearchQuery(''); setShowAddMember(false); } }}>
        <DialogContent className="max-w-4xl max-h-[90vh] w-[95vw] sm:w-auto overflow-y-auto p-3 sm:p-6">
          <DialogHeader className="pb-2">
            <div className="flex items-center justify-between">
              <DialogTitle className="flex flex-wrap items-center gap-2 text-sm sm:text-base">
                {icon}
                <span>{title} List</span>
                <Badge className="text-xs">{data.total || 0}</Badge>
              </DialogTitle>
              {isAdmin && (
                <Button size="sm" onClick={() => setShowAddMember(!showAddMember)} className="bg-navy hover:bg-navy/90">
                  <UserPlus className="w-4 h-4 mr-1" />
                  Add Members
                </Button>
              )}
            </div>
          </DialogHeader>

          {/* Add Members Section - Real-time search */}
          {showAddMember && (
            <div className="border rounded-lg p-3 mb-3 bg-slate-50">
              <div className="flex items-center gap-2 mb-2">
                <Search className="w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Type name or ITS to search (min 1 char)..."
                  value={memberSearchTerm}
                  onChange={(e) => setMemberSearchTerm(e.target.value)}
                  className="flex-1"
                  autoFocus
                />
                {memberSearchTerm && (
                  <button onClick={() => { setMemberSearchTerm(''); setMemberSearchResults([]); }} className="text-slate-400 hover:text-slate-600">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              
              {/* Real-time Search Results */}
              {searchingMembers && <p className="text-xs text-slate-500 mb-2">Searching...</p>}
              
              {memberSearchResults.length > 0 && (
                <div className="max-h-48 overflow-y-auto border rounded bg-white mb-2">
                  {memberSearchResults.map(member => (
                    <div 
                      key={member.id}
                      className={`p-2 border-b last:border-b-0 cursor-pointer hover:bg-slate-50 flex items-center justify-between ${
                        selectedMembers.find(m => m.id === member.id) ? 'bg-blue-50 border-blue-200' : ''
                      }`}
                      onClick={() => toggleMemberSelection(member)}
                    >
                      <div>
                        <p className="text-sm font-medium">{member.full_name}</p>
                        <p className="text-xs text-slate-500">
                          ITS: <span className="font-mono">{member.its_id || '-'}</span> | {member.mohalla || '-'} | {member.mobile || '-'}
                        </p>
                      </div>
                      <Checkbox checked={!!selectedMembers.find(m => m.id === member.id)} />
                    </div>
                  ))}
                </div>
              )}
              
              {memberSearchTerm && !searchingMembers && memberSearchResults.length === 0 && (
                <p className="text-xs text-slate-500 mb-2">No members found matching &ldquo;{memberSearchTerm}&rdquo;</p>
              )}
              
              {/* Selected Members */}
              {selectedMembers.length > 0 && (
                <div className="flex items-center justify-between bg-green-50 p-2 rounded">
                  <div>
                    <p className="text-sm font-medium text-green-700">{selectedMembers.length} member(s) selected</p>
                    <p className="text-xs text-green-600">{selectedMembers.map(m => m.full_name).join(', ')}</p>
                  </div>
                  <Button size="sm" onClick={handleAddMembers} className="bg-green-600 hover:bg-green-700">
                    Add to List
                  </Button>
                </div>
              )}
            </div>
          )}
          
          {/* Search Input */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name, ITS, or mobile..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 text-sm"
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          
          {loading ? (
            <div className="text-center py-8">
              <div className="w-6 h-6 border-2 border-navy border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto -mx-3 sm:mx-0">
                <Table className="min-w-[500px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs sm:text-sm whitespace-nowrap">Sr</TableHead>
                      {/* FMB Lists - HOF ITS and HOF Name headers */}
                      {isFMBList ? (
                        <>
                          <TableHead className="text-xs sm:text-sm whitespace-nowrap">HOF ITS</TableHead>
                          <TableHead className="text-xs sm:text-sm whitespace-nowrap min-w-[150px]">HOF Name</TableHead>
                          <TableHead className="hidden md:table-cell text-xs sm:text-sm whitespace-nowrap">Mohalla</TableHead>
                          <TableHead className="text-xs sm:text-sm whitespace-nowrap">Sector</TableHead>
                          <TableHead className="text-xs sm:text-sm whitespace-nowrap">Thaali No</TableHead>
                          {isAdmin && <TableHead className="text-xs sm:text-sm whitespace-nowrap print:hidden">Size</TableHead>}
                          <TableHead className="text-xs sm:text-sm whitespace-nowrap">Status</TableHead>
                        </>
                      ) : (
                        <>
                          <TableHead className="text-xs sm:text-sm whitespace-nowrap">ITS</TableHead>
                          <TableHead className="text-xs sm:text-sm whitespace-nowrap min-w-[100px]">Name</TableHead>
                          <TableHead className="text-xs sm:text-sm whitespace-nowrap">Mobile</TableHead>
                          {isLeadershipList ? (
                            <>
                              <TableHead className="hidden sm:table-cell text-xs sm:text-sm whitespace-nowrap text-blue-700 font-semibold">
                                Allotted Mohalla
                              </TableHead>
                              {isSectorBasedList && (
                                <TableHead className="hidden md:table-cell text-xs sm:text-sm whitespace-nowrap text-blue-700 font-semibold">
                                  Allotted Sector
                                </TableHead>
                              )}
                            </>
                          ) : (
                            <>
                              <TableHead className="hidden sm:table-cell text-xs sm:text-sm whitespace-nowrap">Mohalla</TableHead>
                              <TableHead className="hidden md:table-cell text-xs sm:text-sm whitespace-nowrap">Sector</TableHead>
                            </>
                          )}
                        </>
                      )}
                      {isAdmin && (
                        <TableHead className="text-xs sm:text-sm whitespace-nowrap text-right">Action</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredMembers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={isAdmin ? (isFMBList ? 9 : 7) : (isFMBList ? 8 : 6)} className="text-center py-8 text-slate-500">
                          {searchQuery ? 'No members match your search' : 'No members in this list'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredMembers.map((member, idx) => (
                        <TableRow key={member.id || member.its_id || idx}>
                          <TableCell className="text-xs sm:text-sm">{idx + 1}</TableCell>
                          {/* FMB Lists - HOF data cells */}
                          {isFMBList ? (
                            <>
                              <TableCell className="font-mono text-sm font-semibold">{member.its_id || '-'}</TableCell>
                              <TableCell className="font-medium text-sm">{member.full_name}</TableCell>
                              <TableCell className="hidden md:table-cell text-sm">{member.mohalla || '-'}</TableCell>
                              <TableCell className="text-sm">{member.sector || '-'}</TableCell>
                              <TableCell className="font-mono font-semibold text-navy text-sm">{member.thaali_number || '-'}</TableCell>
                              {isAdmin && (
                                <TableCell className="print:hidden">
                                  <Badge className={`text-xs ${
                                    member.thaali_size === 'D' ? 'bg-purple-100 text-purple-700' :
                                    member.thaali_size === 'C' ? 'bg-blue-100 text-blue-700' :
                                    member.thaali_size === 'B' ? 'bg-green-100 text-green-700' :
                                    member.thaali_size === 'A' ? 'bg-orange-100 text-orange-700' :
                                    'bg-slate-100 text-slate-500'
                                  }`}>
                                    {member.thaali_size || '-'}
                                  </Badge>
                                </TableCell>
                              )}
                              <TableCell>
                                <Badge className={`text-xs ${member.fmb_status === 'Going' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                  {member.fmb_status || '-'}
                                </Badge>
                              </TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell className="font-mono text-xs">{member.its_id || '-'}</TableCell>
                              <TableCell className="font-medium text-xs sm:text-sm truncate max-w-[120px] sm:max-w-none">{member.full_name}</TableCell>
                              <TableCell className="text-xs sm:text-sm">{member.mobile || '-'}</TableCell>
                              {isLeadershipList ? (
                                <>
                                  <TableCell className="hidden sm:table-cell text-xs sm:text-sm text-blue-700 font-medium">
                                    {member.mohalla || '-'}
                                  </TableCell>
                                  {isSectorBasedList && (
                                    <TableCell className="hidden md:table-cell text-xs sm:text-sm text-blue-700 font-medium">
                                      {member.sector || '-'}
                                    </TableCell>
                                  )}
                                </>
                              ) : (
                                <>
                                  <TableCell className="hidden sm:table-cell text-xs sm:text-sm">{member.mohalla || '-'}</TableCell>
                                  <TableCell className="hidden md:table-cell text-xs sm:text-sm">{member.sector || '-'}</TableCell>
                                </>
                              )}
                            </>
                          )}
                          {isAdmin && (
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveMember(member.id, member.its_id)}
                                disabled={removingMember === (member.id || member.its_id)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 px-2"
                              >
                                {removingMember === (member.id || member.its_id) ? (
                                  <div className="w-3 h-3 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  <Trash2 className="w-3 h-3" />
                                )}
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {!searchQuery && totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-2 border-t">
                  <p className="text-xs sm:text-sm text-slate-500">
                    {page}/{totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Info for leadership lists - clarify this is allotment info */}
              {isLeadershipList && (
                <p className="text-xs text-blue-600 mt-3 text-center bg-blue-50 p-2 rounded">
                  <strong>Note:</strong> The Mohalla/Sector shown above is where this person is <strong>allotted as {title}</strong>, not their personal mohalla.
                </p>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Export Dialog - uses ALL members fetched for export */}
      <ListExportDialog 
        open={showExportDialog}
        onOpenChange={setShowExportDialog}
        listType={title}
        listData={allMembers}
      />
    </>
  );
}

function ViewListDialog({ list, onClose, api }) {
  const [data, setData] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddMember, setShowAddMember] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [allMembers, setAllMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [memberCustomInputs, setMemberCustomInputs] = useState({}); // {memberId: {columnId: value}}
  const [addingMembers, setAddingMembers] = useState(false);
  const [editingCell, setEditingCell] = useState(null); // {memberId, columnId}
  const [cellValue, setCellValue] = useState('');
  const [savingCell, setSavingCell] = useState(false);
  const [showAddColumn, setShowAddColumn] = useState(false);
  const [newColumnName, setNewColumnName] = useState('');
  const [newColumnType, setNewColumnType] = useState('text');
  const [addingColumn, setAddingColumn] = useState(false);
  const [editingColumnId, setEditingColumnId] = useState(null);
  const [editingColumnName, setEditingColumnName] = useState('');
  const [addMode, setAddMode] = useState('search'); // 'search' or 'bulk'
  const [bulkItsInput, setBulkItsInput] = useState('');
  const [bulkResult, setBulkResult] = useState(null);

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      const response = await api.get(`/lists/${list.id}`);
      setData(response.data);
      setMembers(response.data.members || []);
    } catch (error) {
      toast.error('Failed to fetch members');
    } finally {
      setLoading(false);
    }
  };

  // Search members using fuzzy search API
  const searchMembers = async (query) => {
    if (!query || query.length < 1) {
      setAllMembers([]);
      return;
    }
    
    try {
      const response = await api.get(`/members/search-fuzzy?q=${encodeURIComponent(query)}&limit=50`);
      setAllMembers(response.data.members || []);
    } catch (error) {
      console.error('Search failed:', error);
    }
  };

  // Debounced search
  useEffect(() => {
    if (showAddMember && searchTerm) {
      const timer = setTimeout(() => {
        searchMembers(searchTerm);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [searchTerm, showAddMember]);

  const handleOpenAddMember = () => {
    setShowAddMember(true);
    setSelectedMembers([]);
    setMemberCustomInputs({});
    setSearchTerm('');
    setAllMembers([]);
    setAddMode('search');
    setBulkItsInput('');
    setBulkResult(null);
  };

  const toggleMemberSelection = (memberId) => {
    setSelectedMembers(prev => {
      if (prev.includes(memberId)) {
        // Remove from selected and clear custom inputs for this member
        const newInputs = { ...memberCustomInputs };
        delete newInputs[memberId];
        setMemberCustomInputs(newInputs);
        return prev.filter(id => id !== memberId);
      } else {
        return [...prev, memberId];
      }
    });
  };

  const handleCustomInputChange = (memberId, columnId, value) => {
    setMemberCustomInputs(prev => ({
      ...prev,
      [memberId]: {
        ...(prev[memberId] || {}),
        [columnId]: value
      }
    }));
  };

  const handleAddMembers = async () => {
    if (selectedMembers.length === 0) {
      toast.error('Please select at least one member');
      return;
    }

    setAddingMembers(true);
    try {
      // Add members first
      await api.post(`/lists/${list.id}/members`, { member_ids: selectedMembers });
      
      // If there are custom column values, update them
      const customColumns = data?.custom_columns || [];
      if (customColumns.length > 0 && Object.keys(memberCustomInputs).length > 0) {
        // Update member_custom_data for each member that has custom input
        for (const [memberId, columnValues] of Object.entries(memberCustomInputs)) {
          if (Object.keys(columnValues).length > 0) {
            await api.put(`/lists/${list.id}`, {
              member_custom_data: { [memberId]: columnValues }
            });
          }
        }
      }
      
      toast.success(`${selectedMembers.length} member(s) added to list`);
      setShowAddMember(false);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add members');
    } finally {
      setAddingMembers(false);
    }
  };

  const handleBulkItsAdd = async () => {
    if (!bulkItsInput.trim()) {
      toast.error('Please enter ITS numbers');
      return;
    }

    // Parse ITS numbers - split by comma, newline, space, or semicolon
    const itsIds = bulkItsInput
      .split(/[,\n\s;]+/)
      .map(its => its.trim())
      .filter(its => its.length > 0);

    if (itsIds.length === 0) {
      toast.error('No valid ITS numbers found');
      return;
    }

    setAddingMembers(true);
    setBulkResult(null);
    try {
      const response = await api.post(`/lists/${list.id}/members/by-its`, { its_ids: itsIds });
      setBulkResult(response.data);
      
      if (response.data.added > 0) {
        toast.success(`Added ${response.data.added} member(s) to list`);
        fetchMembers();
      } else if (response.data.already_in_list > 0 && response.data.not_found_count === 0) {
        toast.info('All members are already in the list');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add members by ITS');
    } finally {
      setAddingMembers(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    try {
      await api.delete(`/lists/${list.id}/members/${memberId}`);
      toast.success('Member removed from list');
      fetchMembers();
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  // Save cell value for a specific column
  const handleSaveCellData = async () => {
    if (!editingCell) return;
    
    setSavingCell(true);
    try {
      await api.put(`/lists/${list.id}/member-data`, {
        member_id: editingCell.memberId,
        column_id: editingCell.columnId,
        custom_value: cellValue
      });
      toast.success('Saved');
      setEditingCell(null);
      setCellValue('');
      fetchMembers();
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSavingCell(false);
    }
  };

  // Add new column
  const handleAddColumn = async () => {
    if (!newColumnName.trim()) {
      toast.error('Please enter a column name');
      return;
    }
    
    setAddingColumn(true);
    try {
      await api.post(`/lists/${list.id}/columns`, { name: newColumnName.trim(), type: newColumnType });
      toast.success('Column added');
      setShowAddColumn(false);
      setNewColumnName('');
      setNewColumnType('text');
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add column');
    } finally {
      setAddingColumn(false);
    }
  };

  // Update column name
  const handleUpdateColumnName = async (columnId) => {
    if (!editingColumnName.trim()) {
      toast.error('Please enter a column name');
      return;
    }
    
    try {
      await api.put(`/lists/${list.id}/columns/${columnId}`, { name: editingColumnName.trim() });
      toast.success('Column renamed');
      setEditingColumnId(null);
      setEditingColumnName('');
      fetchMembers();
    } catch (error) {
      toast.error('Failed to rename column');
    }
  };

  // Delete column
  const handleDeleteColumn = async (columnId, columnName) => {
    if (!window.confirm(`Delete column "${columnName}"? All data in this column will be lost.`)) return;
    
    try {
      await api.delete(`/lists/${list.id}/columns/${columnId}`);
      toast.success('Column deleted');
      fetchMembers();
    } catch (error) {
      toast.error('Failed to delete column');
    }
  };

  // Toggle column type between text and checkbox
  const handleToggleColumnType = async (columnId, currentType) => {
    const newType = currentType === 'checkbox' ? 'text' : 'checkbox';
    try {
      await api.put(`/lists/${list.id}/columns/${columnId}/type`, { type: newType });
      toast.success(`Column type changed to ${newType === 'checkbox' ? 'checkbox' : 'text'}`);
      fetchMembers();
    } catch (error) {
      toast.error('Failed to change column type');
    }
  };

  // Get columns and member data
  const customColumns = data?.custom_columns || [];
  const memberCustomData = data?.member_custom_data || {};

  // Helper to get cell value
  const getCellValue = (memberId, columnId) => {
    const memberData = memberCustomData[memberId];
    if (!memberData) return '';
    if (typeof memberData === 'string') {
      // Legacy single value - show in first column
      return columnId === customColumns[0]?.id ? memberData : '';
    }
    return memberData[columnId] || '';
  };

  // Filter out members already in the list
  const existingMemberIds = members.map(m => m.id);
  const filteredMembers = allMembers.filter(m => !existingMemberIds.includes(m.id));

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl w-[95vw] max-h-[90vh] overflow-y-auto p-3 sm:p-6">
        <DialogHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
              <List className="w-4 h-4 sm:w-5 sm:h-5 text-navy" />
              <span className="truncate max-w-[150px] sm:max-w-none">{list.name}</span>
              <Badge variant="secondary" className="text-xs">{members.length}</Badge>
            </DialogTitle>
            <div className="flex gap-1 sm:gap-2 flex-wrap">
              <Button size="sm" variant="outline" onClick={() => setShowAddColumn(true)} className="text-xs px-2">
                <Plus className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                <span className="hidden sm:inline">Column</span>
              </Button>
              <Button size="sm" variant="outline" onClick={() => setShowExportDialog(true)} className="text-xs px-2">
                <Download className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                <span className="hidden sm:inline">Export</span>
              </Button>
              <Button size="sm" onClick={handleOpenAddMember} className="bg-navy hover:bg-navy/90 text-xs px-2">
                <UserPlus className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                <span className="hidden sm:inline">Add</span>
              </Button>
            </div>
          </div>
          {customColumns.length > 0 && (
            <p className="text-xs text-slate-500 mt-1 hidden sm:block">
              Custom columns: {customColumns.map(c => c.name).join(', ')}
            </p>
          )}
        </DialogHeader>
        
        {loading ? (
          <div className="text-center py-8">
            <div className="w-6 h-6 border-2 border-navy border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : members.length === 0 ? (
          <div className="text-center py-8">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No members in this list</p>
            <Button onClick={handleOpenAddMember} className="mt-4 bg-navy">
              <UserPlus className="w-4 h-4 mr-2" />
              Add Members
            </Button>
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="block sm:hidden space-y-2">
              {members.map((member, idx) => (
                <div key={member.id || idx} className="bg-slate-50 rounded-lg p-3 border text-sm">
                  <div className="flex justify-between items-start">
                    <div className="flex items-start gap-2 flex-1 min-w-0">
                      <span className="bg-navy text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0">
                        {idx + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{member.full_name}</p>
                        <p className="text-xs text-slate-500 font-mono">{member.its_id}</p>
                        <p className="text-xs text-slate-500 mt-1">{member.mohalla} • {member.sector}</p>
                        {member.mobile && <p className="text-xs text-slate-500">{member.mobile}</p>}
                      </div>
                    </div>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="text-red-500 h-8 w-8 p-0"
                      onClick={() => handleRemoveMember(member.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  {customColumns.length > 0 && (
                    <div className="mt-2 pt-2 border-t space-y-2">
                      {customColumns.map(col => (
                        <div key={col.id} className="text-xs">
                          <span className="text-emerald-700 font-medium block mb-1">{col.name}:</span>
                          {col.type === 'checkbox' ? (
                            <Checkbox
                              checked={getCellValue(member.id, col.id) === 'true'}
                              onCheckedChange={async (checked) => {
                                try {
                                  await api.put(`/lists/${list.id}/member-data`, {
                                    member_id: member.id,
                                    custom_value: checked ? 'true' : 'false',
                                    column_id: col.id
                                  });
                                  toast.success('Saved');
                                  fetchMembers();
                                } catch (error) {
                                  toast.error('Failed to update');
                                }
                              }}
                            />
                          ) : editingCell?.memberId === member.id && editingCell?.columnId === col.id ? (
                            <div className="flex gap-1 items-center">
                              <Input
                                value={cellValue}
                                onChange={(e) => setCellValue(e.target.value)}
                                className="h-8 flex-1 text-sm"
                                placeholder="Enter value"
                                autoFocus
                                onBlur={handleSaveCellData}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleSaveCellData();
                                  if (e.key === 'Escape') setEditingCell(null);
                                }}
                              />
                              {savingCell && <Loader2 className="w-4 h-4 animate-spin text-navy" />}
                            </div>
                          ) : (
                            <div 
                              className="cursor-pointer bg-white border rounded px-3 py-2 w-full text-sm"
                              onClick={() => {
                                setEditingCell({ memberId: member.id, columnId: col.id });
                                setCellValue(getCellValue(member.id, col.id));
                              }}
                            >
                              {getCellValue(member.id, col.id) || <span className="text-slate-400">Tap to enter</span>}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            {/* Desktop Table View */}
            <div className="hidden sm:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">Sr</TableHead>
                    <TableHead className="w-24">ITS ID</TableHead>
                    <TableHead className="min-w-[150px]">Name</TableHead>
                    <TableHead className="w-28">Mobile</TableHead>
                    <TableHead className="w-32">Mohalla</TableHead>
                    <TableHead className="w-24">Sector</TableHead>
                    {customColumns.map(col => (
                      <TableHead key={col.id} className="bg-emerald-50 min-w-[120px]">
                        <div className="flex items-center gap-1">
                          {editingColumnId === col.id ? (
                            <div className="flex gap-1">
                              <Input
                                value={editingColumnName}
                                onChange={(e) => setEditingColumnName(e.target.value)}
                                className="h-6 w-20 text-xs"
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleUpdateColumnName(col.id);
                                  if (e.key === 'Escape') setEditingColumnId(null);
                                }}
                                autoFocus
                              />
                              <Button size="sm" variant="ghost" className="h-6 px-1" onClick={() => handleUpdateColumnName(col.id)}>✓</Button>
                              <Button size="sm" variant="ghost" className="h-6 px-1" onClick={() => setEditingColumnId(null)}>✕</Button>
                            </div>
                          ) : (
                            <>
                              {col.type === 'checkbox' && <span className="text-xs mr-1">☑</span>}
                              <span className="text-emerald-700">{col.name}</span>
                              <button
                                className="opacity-50 hover:opacity-100 text-slate-500"
                                onClick={() => { setEditingColumnId(col.id); setEditingColumnName(col.name); }}
                                title="Rename column"
                              >
                                <Edit className="w-3 h-3" />
                              </button>
                              <button
                                className="opacity-50 hover:opacity-100 text-blue-500"
                                onClick={() => handleToggleColumnType(col.id, col.type)}
                                title={col.type === 'checkbox' ? 'Change to text' : 'Change to checkbox'}
                              >
                                {col.type === 'checkbox' ? 'T' : '☑'}
                              </button>
                              <button
                                className="opacity-50 hover:opacity-100 text-red-500"
                                onClick={() => handleDeleteColumn(col.id, col.name)}
                                title="Delete column"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </>
                          )}
                        </div>
                      </TableHead>
                    ))}
                    <TableHead className="text-right w-16">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                {members.map((member, idx) => (
                  <TableRow key={member.id || idx}>
                    <TableCell className="text-sm">{idx + 1}</TableCell>
                    <TableCell className="font-mono text-sm">{member.its_id || '-'}</TableCell>
                    <TableCell className="font-medium">{member.full_name}</TableCell>
                    <TableCell className="text-sm">{member.mobile || '-'}</TableCell>
                    <TableCell className="text-sm">{member.mohalla || '-'}</TableCell>
                    <TableCell className="text-sm">{member.sector || '-'}</TableCell>
                    {customColumns.map(col => (
                      <TableCell key={col.id} className="bg-emerald-50/50">
                        {col.type === 'checkbox' ? (
                          <div className="flex justify-center">
                            <Checkbox
                              checked={getCellValue(member.id, col.id) === 'true' || getCellValue(member.id, col.id) === true}
                              onCheckedChange={async (checked) => {
                                try {
                                  await api.put(`/lists/${list.id}/member-data`, {
                                    member_id: member.id,
                                    custom_value: checked ? 'true' : 'false',
                                    column_id: col.id
                                  });
                                  toast.success('Saved');
                                  fetchMembers();
                                } catch (error) {
                                  toast.error('Failed to update');
                                }
                              }}
                            />
                          </div>
                        ) : editingCell?.memberId === member.id && editingCell?.columnId === col.id ? (
                          <div className="flex gap-1 items-center">
                            <Input
                              value={cellValue}
                              onChange={(e) => setCellValue(e.target.value)}
                              className="h-7 w-full text-sm"
                              placeholder="Enter value"
                              onBlur={handleSaveCellData}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSaveCellData();
                                if (e.key === 'Escape') setEditingCell(null);
                              }}
                              autoFocus
                            />
                            {savingCell && <Loader2 className="w-4 h-4 animate-spin text-navy" />}
                          </div>
                        ) : (
                          <div 
                            className="cursor-pointer hover:bg-emerald-100 rounded px-2 py-1 min-h-[28px] flex items-center"
                            onClick={() => {
                              setEditingCell({ memberId: member.id, columnId: col.id });
                              setCellValue(getCellValue(member.id, col.id));
                            }}
                          >
                            {getCellValue(member.id, col.id) || <span className="text-slate-400 text-xs">Click to edit</span>}
                          </div>
                        )}
                      </TableCell>
                    ))}
                    <TableCell className="text-right">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-red-600 hover:text-red-700 h-7 px-2"
                        onClick={() => handleRemoveMember(member.id)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          </>
        )}

        {/* Add Column Dialog */}
        <Dialog open={showAddColumn} onOpenChange={setShowAddColumn}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Add Custom Column</DialogTitle>
              <DialogDescription>
                Add a new column to track additional information for each member.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Column Name *</Label>
                <Input
                  value={newColumnName}
                  onChange={(e) => setNewColumnName(e.target.value)}
                  placeholder="e.g., Amount, Notes, Status"
                  onKeyDown={(e) => e.key === 'Enter' && handleAddColumn()}
                />
              </div>
              <div>
                <Label>Column Type</Label>
                <Select value={newColumnType} onValueChange={setNewColumnType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">Text</SelectItem>
                    <SelectItem value="checkbox">Tick / Checkbox ☑</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddColumn(false)}>Cancel</Button>
              <Button onClick={handleAddColumn} disabled={addingColumn} className="bg-navy">
                {addingColumn ? 'Adding...' : 'Add Column'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Export Dialog */}
        <ListExportDialog 
          open={showExportDialog}
          onOpenChange={setShowExportDialog}
          listType={list.name}
          listData={members}
          customColumns={customColumns}
          memberCustomData={memberCustomData}
        />

        {/* Add Members Sub-Dialog */}
        {showAddMember && (
          <Dialog open={showAddMember} onOpenChange={setShowAddMember}>
            <DialogContent className="max-w-2xl max-h-[80vh]">
              <DialogHeader>
                <DialogTitle>Add Members to {list.name}</DialogTitle>
              </DialogHeader>
              
              {/* Mode Toggle */}
              <div className="flex gap-2 border-b pb-3">
                <Button 
                  variant={addMode === 'search' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => { setAddMode('search'); setBulkResult(null); }}
                  className={addMode === 'search' ? 'bg-navy' : ''}
                >
                  <Search className="w-4 h-4 mr-1" />
                  Search
                </Button>
                <Button 
                  variant={addMode === 'bulk' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => { setAddMode('bulk'); setBulkResult(null); }}
                  className={addMode === 'bulk' ? 'bg-navy' : ''}
                >
                  <List className="w-4 h-4 mr-1" />
                  Bulk ITS
                </Button>
              </div>

              {/* Search Mode */}
              {addMode === 'search' && (
                <div className="space-y-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search by name, ITS number, or mobile..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9"
                      autoFocus
                    />
                  </div>

                  {!searchTerm && (
                    <div className="bg-slate-50 border rounded p-3 text-sm text-slate-600 text-center">
                      Type name or ITS number to search members
                    </div>
                  )}

                  {selectedMembers.length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded p-2 text-sm text-blue-700">
                      {selectedMembers.length} member(s) selected
                    </div>
                  )}

                  <div className="border rounded-lg max-h-64 overflow-y-auto">
                    {filteredMembers.slice(0, 100).map((member) => {
                      const isSelected = selectedMembers.includes(member.id);
                      const customColumns = data?.custom_columns || [];
                      return (
                        <div key={member.id} className="border-b last:border-b-0">
                          <div 
                            className={`flex items-center justify-between p-3 cursor-pointer hover:bg-slate-50 ${isSelected ? 'bg-blue-50' : ''}`}
                            onClick={() => toggleMemberSelection(member.id)}
                          >
                            <div>
                              <p className="font-medium">{member.full_name}</p>
                              <p className="text-xs text-slate-500">
                                <span className="font-mono text-emerald-600">{member.its_id || 'N/A'}</span>
                                {' • '}
                                {member.mohalla || 'N/A'} - {member.sector || 'N/A'}
                              </p>
                            </div>
                            <input 
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => {}}
                              className="w-4 h-4"
                            />
                          </div>
                          {/* Custom column inputs for selected member */}
                          {isSelected && customColumns.length > 0 && (
                            <div className="px-3 pb-3 pt-1 bg-blue-50/50 border-t border-blue-100">
                              <p className="text-xs text-slate-500 mb-2">Add custom data:</p>
                              <div className="grid grid-cols-2 gap-2">
                                {customColumns.map(col => (
                                  <div key={col.id} className="flex items-center gap-2">
                                    {col.type === 'checkbox' ? (
                                      <label className="flex items-center gap-2 text-sm">
                                        <input
                                          type="checkbox"
                                          checked={memberCustomInputs[member.id]?.[col.id] === 'true'}
                                          onChange={(e) => handleCustomInputChange(member.id, col.id, e.target.checked ? 'true' : '')}
                                          onClick={(e) => e.stopPropagation()}
                                          className="w-4 h-4"
                                        />
                                        {col.name}
                                      </label>
                                    ) : (
                                      <Input
                                        placeholder={col.name}
                                        value={memberCustomInputs[member.id]?.[col.id] || ''}
                                        onChange={(e) => handleCustomInputChange(member.id, col.id, e.target.value)}
                                        onClick={(e) => e.stopPropagation()}
                                        className="h-8 text-sm"
                                      />
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {searchTerm && filteredMembers.length === 0 && (
                      <div className="p-4 text-center text-slate-500">
                        No members found for "{searchTerm}"
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Bulk ITS Mode */}
              {addMode === 'bulk' && (
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium mb-2 block">
                      Enter ITS Numbers (separated by comma, space, or new line)
                    </Label>
                    <textarea
                      className="w-full h-32 p-3 border rounded-lg text-sm font-mono resize-none focus:outline-none focus:ring-2 focus:ring-navy"
                      placeholder="20348602, 40400165, 30401085&#10;20404314&#10;50462579, 60417625"
                      value={bulkItsInput}
                      onChange={(e) => setBulkItsInput(e.target.value)}
                      autoFocus
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      {bulkItsInput.split(/[,\n\s;]+/).filter(its => its.trim().length > 0).length} ITS number(s) entered
                    </p>
                  </div>

                  {/* Bulk Result */}
                  {bulkResult && (
                    <div className="space-y-2">
                      {bulkResult.added > 0 && (
                        <div className="bg-green-50 border border-green-200 rounded p-2 text-sm text-green-700">
                          ✓ {bulkResult.added} member(s) added successfully
                        </div>
                      )}
                      {bulkResult.already_in_list > 0 && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded p-2 text-sm text-yellow-700">
                          ⚠ {bulkResult.already_in_list} member(s) already in list
                        </div>
                      )}
                      {bulkResult.not_found_count > 0 && (
                        <div className="bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700">
                          ✗ {bulkResult.not_found_count} ITS not found: {bulkResult.not_found.slice(0, 10).join(', ')}
                          {bulkResult.not_found.length > 10 && ` ... and ${bulkResult.not_found.length - 10} more`}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAddMember(false)}>Cancel</Button>
                {addMode === 'search' ? (
                  <Button 
                    onClick={handleAddMembers} 
                    disabled={selectedMembers.length === 0 || addingMembers}
                    className="bg-navy"
                  >
                    {addingMembers ? 'Adding...' : `Add ${selectedMembers.length} Member(s)`}
                  </Button>
                ) : (
                  <Button 
                    onClick={handleBulkItsAdd} 
                    disabled={!bulkItsInput.trim() || addingMembers}
                    className="bg-navy"
                  >
                    {addingMembers ? 'Adding...' : 'Add by ITS'}
                  </Button>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CreateListDialog({ api, onClose, onCreated, isAdmin, existingFolders = [] }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [folder, setFolder] = useState('');
  const [newFolder, setNewFolder] = useState('');
  const [assignedManagers, setAssignedManagers] = useState([]);
  const [newManagerIts, setNewManagerIts] = useState('');
  const [columns, setColumns] = useState([]);
  const [newColumnName, setNewColumnName] = useState('');
  const [newColumnType, setNewColumnType] = useState('text');
  const [loading, setLoading] = useState(false);

  const handleAddManager = () => {
    const its = newManagerIts.trim();
    if (!its || its.length !== 8) {
      toast.error('Please enter a valid 8-digit ITS ID');
      return;
    }
    if (assignedManagers.includes(its)) {
      toast.error('Manager already added');
      return;
    }
    setAssignedManagers([...assignedManagers, its]);
    setNewManagerIts('');
  };

  const handleRemoveManager = (its) => {
    setAssignedManagers(assignedManagers.filter(m => m !== its));
  };

  const handleAddColumn = () => {
    if (!newColumnName.trim()) return;
    if (columns.some(c => c.name.toLowerCase() === newColumnName.trim().toLowerCase())) {
      toast.error('Column already exists');
      return;
    }
    setColumns([...columns, { id: `temp-${Date.now()}`, name: newColumnName.trim(), type: newColumnType }]);
    setNewColumnName('');
    setNewColumnType('text');
  };

  const handleRemoveColumn = (id) => {
    setColumns(columns.filter(c => c.id !== id));
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error('Please enter a list name');
      return;
    }

    try {
      setLoading(true);
      const finalFolder = newFolder.trim() || folder;
      await api.post('/lists', { 
        name, 
        description,
        folder: finalFolder,
        assigned_managers: assignedManagers,
        custom_columns: columns.map(c => ({ id: c.id, name: c.name, type: c.type }))
      });
      toast.success('List created');
      onCreated();
      onClose();
    } catch (error) {
      toast.error('Failed to create list');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New List</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>List Name *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter list name"
            />
          </div>
          <div>
            <Label>Description</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter description (optional)"
            />
          </div>
          
          {/* Folder Selection */}
          <div>
            <Label className="flex items-center gap-2">
              <Folder className="w-4 h-4" />
              Folder / Category
            </Label>
            <p className="text-xs text-slate-500 mb-2">Organize lists into folders</p>
            <div className="flex gap-2">
              {existingFolders.length > 0 ? (
                <Select value={folder} onValueChange={(v) => { setFolder(v === '__none__' ? '' : v); setNewFolder(''); }}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Select folder" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No Folder</SelectItem>
                    {existingFolders.map(f => (
                      <SelectItem key={f.id || f.name || f} value={f.name || f}>{f.name || f}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : null}
              <Input
                value={newFolder}
                onChange={(e) => { setNewFolder(e.target.value); setFolder(''); }}
                placeholder="Or create new folder"
                className="flex-1"
              />
            </div>
          </div>
          
          {/* Assigned List Managers - Admin Only */}
          {isAdmin && (
            <div>
              <Label className="flex items-center gap-2">
                <UserCog className="w-4 h-4" />
                Assign List Managers
              </Label>
              <p className="text-xs text-slate-500 mb-2">Enter ITS IDs of users who can access this list</p>
              
              {assignedManagers.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {assignedManagers.map(its => (
                    <Badge key={its} variant="secondary" className="flex items-center gap-1 pl-3 pr-1 py-1 bg-blue-100">
                      {its}
                      <button
                        onClick={() => handleRemoveManager(its)}
                        className="ml-1 hover:bg-blue-300 rounded-full p-0.5"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
              
              <div className="flex gap-2">
                <Input
                  value={newManagerIts}
                  onChange={(e) => setNewManagerIts(e.target.value.replace(/\D/g, '').slice(0, 8))}
                  placeholder="Enter 8-digit ITS ID"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddManager())}
                  className="flex-1"
                />
                <Button type="button" variant="outline" onClick={handleAddManager} disabled={newManagerIts.length !== 8}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
          
          <div>
            <Label>Custom Columns</Label>
            <p className="text-xs text-slate-500 mb-2">Add columns to track additional info per member</p>
            
            {columns.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {columns.map(col => (
                  <Badge key={col.id} variant="secondary" className="flex items-center gap-1 pl-3 pr-1 py-1">
                    {col.type === 'checkbox' ? '☑ ' : ''}{col.name}
                    <button
                      onClick={() => handleRemoveColumn(col.id)}
                      className="ml-1 hover:bg-slate-300 rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
            
            <div className="flex gap-2">
              <Input
                value={newColumnName}
                onChange={(e) => setNewColumnName(e.target.value)}
                placeholder="e.g., Amount, Notes, Status"
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddColumn())}
                className="flex-1"
              />
              <Select value={newColumnType} onValueChange={setNewColumnType}>
                <SelectTrigger className="w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Text</SelectItem>
                  <SelectItem value="checkbox">Tick ☑</SelectItem>
                </SelectContent>
              </Select>
              <Button type="button" variant="outline" onClick={handleAddColumn} disabled={!newColumnName.trim()}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleCreate} disabled={loading} className="bg-navy">
            {loading ? 'Creating...' : 'Create List'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Edit List Dialog Component
function EditListDialog({ list, api, onClose, onUpdated }) {
  const [name, setName] = useState(list.name || '');
  const [description, setDescription] = useState(list.description || '');
  const [loading, setLoading] = useState(false);

  const handleUpdate = async () => {
    if (!name.trim()) {
      toast.error('List name is required');
      return;
    }

    setLoading(true);
    try {
      await api.put(`/lists/${list.id}`, {
        name: name.trim(),
        description: description.trim()
      });
      toast.success('List updated successfully');
      onUpdated();
      onClose();
    } catch (error) {
      toast.error('Failed to update list');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="w-5 h-5 text-navy" />
            Edit List
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>List Name *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter list name"
              className="mt-1"
            />
          </div>
          <div>
            <Label>Description</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter description (optional)"
              className="mt-1"
            />
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleUpdate} disabled={loading} className="bg-navy">
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Dialog to manage list access - assign/remove list managers
function ManageListAccessDialog({ list, api, onClose, onUpdated }) {
  const [assignedManagers, setAssignedManagers] = useState(list.assigned_managers || []);
  const [newManagerIts, setNewManagerIts] = useState('');
  const [loading, setLoading] = useState(false);
  const [memberInfo, setMemberInfo] = useState({});

  // Look up member info when component mounts
  useEffect(() => {
    const fetchMemberInfo = async () => {
      if (assignedManagers.length === 0) return;
      
      try {
        // Fetch member info for each ITS
        const info = {};
        for (const its of assignedManagers) {
          try {
            const res = await api.get(`/members/by-its/${its}`);
            if (res.data) {
              info[its] = res.data.full_name || res.data.name || its;
            }
          } catch {
            info[its] = its;
          }
        }
        setMemberInfo(info);
      } catch (error) {
        console.error('Failed to fetch member info:', error);
      }
    };
    
    fetchMemberInfo();
  }, [assignedManagers, api]);

  const handleAddManager = async () => {
    const its = newManagerIts.trim();
    if (!its || its.length !== 8) {
      toast.error('Please enter a valid 8-digit ITS ID');
      return;
    }
    if (assignedManagers.includes(its)) {
      toast.error('Manager already added');
      return;
    }
    
    // Look up member name
    try {
      const res = await api.get(`/members/by-its/${its}`);
      if (res.data) {
        setMemberInfo(prev => ({ ...prev, [its]: res.data.full_name || res.data.name || its }));
      }
    } catch {
      // User might not exist in members but can still be assigned
    }
    
    setAssignedManagers([...assignedManagers, its]);
    setNewManagerIts('');
  };

  const handleRemoveManager = (its) => {
    setAssignedManagers(assignedManagers.filter(m => m !== its));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      await api.put(`/lists/${list.id}`, {
        assigned_managers: assignedManagers
      });
      toast.success('List access updated');
      onUpdated();
      onClose();
    } catch (error) {
      toast.error('Failed to update list access');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCog className="w-5 h-5 text-blue-600" />
            Manage Access: {list.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              List Managers with Access
            </Label>
            <p className="text-xs text-slate-500 mb-2">
              These users can view and edit this list (enter ITS ID)
            </p>
            
            {assignedManagers.length > 0 ? (
              <div className="space-y-2 mb-3">
                {assignedManagers.map(its => (
                  <div key={its} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                    <div>
                      <span className="font-medium">{its}</span>
                      {memberInfo[its] && memberInfo[its] !== its && (
                        <span className="text-sm text-slate-500 ml-2">({memberInfo[its]})</span>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveManager(its)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400 mb-3 py-2">No managers assigned yet</p>
            )}
            
            <div className="flex gap-2">
              <Input
                value={newManagerIts}
                onChange={(e) => setNewManagerIts(e.target.value.replace(/\D/g, '').slice(0, 8))}
                placeholder="Enter 8-digit ITS ID"
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddManager())}
                className="flex-1"
              />
              <Button type="button" variant="outline" onClick={handleAddManager} disabled={newManagerIts.length !== 8}>
                <Plus className="w-4 h-4 mr-1" />
                Add
              </Button>
            </div>
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={loading} className="bg-navy">
            {loading ? 'Saving...' : 'Save Access'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Manage Folder Content Component
function ManageFolderContent({ folder, api, onDelete, onUpdate, onClose }) {
  const [renameTo, setRenameTo] = useState(folder.name);
  const [selectedAutoTypes, setSelectedAutoTypes] = useState(folder.auto_list_types || []);

  const ALL_AUTO_LIST_TYPES = [
    { type: 'mashaaikh', label: 'Mashaaikh' },
    { type: 'mafasiyah', label: 'Mafasiyah' },
    { type: 'fmb-all', label: 'FMB - All' },
    { type: 'fmb-going', label: 'FMB - Going' },
    { type: 'fmb-not-going', label: 'FMB - Not Going' },
    { type: 'managers', label: 'Managers Jamaat' },
    { type: 'mustashareen', label: 'Mustashareen Jamaat' },
    { type: 'masool', label: 'Masool' },
    { type: 'masoola', label: 'Masoola' },
    { type: 'musaid', label: 'Musaid' },
    { type: 'musaida', label: 'Musaida' },
    { type: 'fmb-incharge', label: 'FMB Incharge' },
    { type: 'umoor-coordinators', label: 'Umoor Coordinators' },
  ];

  const toggleAutoType = (t) => {
    setSelectedAutoTypes(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  };

  return (
    <div className="space-y-4 py-2">
      {/* Rename */}
      <div>
        <label className="text-sm font-medium text-slate-700">Rename Folder</label>
        <div className="flex gap-2 mt-1">
          <Input value={renameTo} onChange={(e) => setRenameTo(e.target.value)} />
          <Button 
            variant="outline" 
            disabled={!renameTo.trim() || renameTo === folder.name}
            onClick={() => onUpdate({ name: renameTo.trim() })}
          >
            Rename
          </Button>
        </div>
      </div>

      {/* Auto List Assignment (only for auto folders) */}
      {folder.tab === 'auto' && (
        <div>
          <label className="text-sm font-medium text-slate-700">Auto Lists in this Folder</label>
          <div className="grid grid-cols-2 gap-2 mt-2 max-h-60 overflow-y-auto">
            {ALL_AUTO_LIST_TYPES.map(al => (
              <label key={al.type} className="flex items-center gap-2 text-sm cursor-pointer p-2 rounded hover:bg-slate-50">
                <input 
                  type="checkbox" 
                  checked={selectedAutoTypes.includes(al.type)}
                  onChange={() => toggleAutoType(al.type)}
                  className="rounded border-slate-300"
                />
                {al.label}
              </label>
            ))}
          </div>
          <Button 
            className="mt-3 bg-navy hover:bg-navy-light" 
            size="sm"
            onClick={() => onUpdate({ auto_list_types: selectedAutoTypes })}
          >
            Save Auto List Assignment
          </Button>
        </div>
      )}

      {/* Delete folder */}
      <div className="border-t pt-4">
        <Button variant="destructive" size="sm" onClick={onDelete}>
          <Trash2 className="w-4 h-4 mr-2" />
          Delete Folder
        </Button>
        <p className="text-xs text-slate-500 mt-1">Lists inside will be moved to unfiled.</p>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Close</Button>
      </DialogFooter>
    </div>
  );
}
